import React, { useEffect, useState } from 'react';
import { Header } from '@/components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { complianceAPI, complianceControlsAPI, reportsAPI } from '@/lib/api';
import { cn } from '@/lib/utils';
import { CheckCircle, XCircle, AlertTriangle, FileCheck, Download, Shield, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const frameworkInfo = {
  lgpd: { 
    name: 'LGPD', 
    fullName: 'Lei Geral de Proteção de Dados',
    description: 'Regulamentação brasileira de proteção de dados pessoais'
  },
  iso27001: { 
    name: 'ISO 27001', 
    fullName: 'ISO/IEC 27001:2022',
    description: 'Sistema de Gestão de Segurança da Informação'
  },
  nist: { 
    name: 'NIST CSF', 
    fullName: 'NIST Cybersecurity Framework',
    description: 'Framework de cibersegurança do NIST'
  },
  cis: { 
    name: 'CIS Controls', 
    fullName: 'CIS Critical Security Controls v8',
    description: 'Controles críticos de segurança do CIS'
  },
  'pci-dss': { 
    name: 'PCI-DSS', 
    fullName: 'Payment Card Industry Data Security Standard',
    description: 'Padrão de segurança para dados de cartões'
  },
};

export function CompliancePage() {
  const [compliance, setCompliance] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedFramework, setSelectedFramework] = useState(null);
  const [controls, setControls] = useState([]);
  const [controlsLoading, setControlsLoading] = useState(false);
  const [controlFilter, setControlFilter] = useState('all');
  const [exportingPDF, setExportingPDF] = useState(false);

  useEffect(() => { loadCompliance(); }, []);

  const loadCompliance = async () => {
    try {
      const response = await complianceAPI.getAll();
      setCompliance(response.data.compliance);
    } catch (error) {
      console.error('Error loading compliance:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFrameworkClick = async (item) => {
    setSelectedFramework(item);
    setControlsLoading(true);
    setControlFilter('all');
    try {
      const res = await complianceControlsAPI.getControls(item.framework);
      setControls(res.data.controls || []);
    } catch (error) {
      console.error('Error loading controls:', error);
    } finally {
      setControlsLoading(false);
    }
  };

  const handleExportPDF = async (framework) => {
    setExportingPDF(true);
    try {
      const res = await reportsAPI.getCompliancePDF(framework || undefined);
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `compliance_${framework || 'all'}_${new Date().toISOString().substring(0,10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Relatório PDF exportado!');
    } catch {
      toast.error('Erro ao exportar PDF');
    } finally {
      setExportingPDF(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 90) return 'text-success';
    if (score >= 70) return 'text-info';
    if (score >= 50) return 'text-warning';
    return 'text-destructive';
  };

  const filteredControls = controlFilter === 'all' ? controls : controls.filter(c => c.status === controlFilter);
  const passedControls = controls.filter(c => c.status === 'passed');
  const failedControls = controls.filter(c => c.status === 'failed');

  return (
    <div className="min-h-screen" data-testid="compliance-page">
      <Header title="Compliance" />
      
      <div className="p-6 space-y-6">
        {/* Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {compliance.map((item) => {
            const info = frameworkInfo[item.framework] || { name: item.framework.toUpperCase() };
            const isCompliant = item.score >= 80;
            
            return (
              <Card 
                key={item.id} 
                className={cn(
                  "bg-card/50 border-border hover:border-primary/50 transition-all cursor-pointer group",
                  isCompliant ? "border-success/30" : "border-warning/30"
                )}
                onClick={() => handleFrameworkClick(item)}
                data-testid={`compliance-framework-${item.framework}`}
              >
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="font-display text-lg uppercase tracking-wider group-hover:text-primary transition-colors">
                        {info.name}
                      </h3>
                      <p className="text-xs text-muted-foreground mt-1">{info.fullName}</p>
                    </div>
                    {isCompliant ? (
                      <CheckCircle className="h-6 w-6 text-success" />
                    ) : (
                      <AlertTriangle className="h-6 w-6 text-warning" />
                    )}
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Score</span>
                      <span className={cn("text-2xl font-mono font-bold", getScoreColor(item.score))}>
                        {item.score}%
                      </span>
                    </div>
                    <Progress value={item.score} className="h-2" />
                    
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-1 text-success">
                        <CheckCircle className="h-3 w-3" />
                        <span>{item.passed_controls} passed</span>
                      </div>
                      <div className="flex items-center gap-1 text-destructive">
                        <XCircle className="h-3 w-3" />
                        <span>{item.failed_controls} failed</span>
                      </div>
                    </div>
                    <p className="text-xs text-primary opacity-0 group-hover:opacity-100 transition-opacity text-center">
                      Clique para ver controles detalhados
                    </p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Detailed View */}
        <Card className="bg-card/50 border-border">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-display text-sm uppercase tracking-wider text-muted-foreground">
              Visão Geral de Compliance
            </CardTitle>
            <Button variant="outline" size="sm" onClick={() => handleExportPDF()} disabled={exportingPDF} data-testid="export-compliance-pdf-btn">
              {exportingPDF ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
              Exportar Relatório PDF
            </Button>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {compliance.map((item) => {
                const info = frameworkInfo[item.framework] || { name: item.framework.toUpperCase() };
                
                return (
                  <div 
                    key={item.id}
                    className="p-4 bg-secondary/50 rounded-sm border border-border hover:border-primary/50 transition-colors cursor-pointer"
                    onClick={() => handleFrameworkClick(item)}
                    data-testid={`compliance-detail-${item.framework}`}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <FileCheck className="h-5 w-5 text-primary" />
                        <div>
                          <h4 className="font-display uppercase tracking-wider">{info.name}</h4>
                          <p className="text-xs text-muted-foreground">{info.description}</p>
                        </div>
                      </div>
                      <Badge className={cn(
                        "font-mono",
                        item.score >= 80 ? "bg-success/20 text-success" : "bg-warning/20 text-warning"
                      )}>
                        {item.score >= 80 ? 'COMPLIANT' : 'NON-COMPLIANT'}
                      </Badge>
                    </div>
                    
                    <div className="grid grid-cols-4 gap-4">
                      <div className="text-center p-3 bg-background/50 rounded-sm">
                        <p className="text-2xl font-mono font-bold">{item.total_controls}</p>
                        <p className="text-xs text-muted-foreground">Total Controls</p>
                      </div>
                      <div className="text-center p-3 bg-success/10 rounded-sm">
                        <p className="text-2xl font-mono font-bold text-success">{item.passed_controls}</p>
                        <p className="text-xs text-muted-foreground">Passed</p>
                      </div>
                      <div className="text-center p-3 bg-destructive/10 rounded-sm">
                        <p className="text-2xl font-mono font-bold text-destructive">{item.failed_controls}</p>
                        <p className="text-xs text-muted-foreground">Failed</p>
                      </div>
                      <div className="text-center p-3 bg-background/50 rounded-sm">
                        <p className={cn("text-2xl font-mono font-bold", getScoreColor(item.score))}>{item.score}%</p>
                        <p className="text-xs text-muted-foreground">Score</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Controls Detail Modal */}
      <Dialog open={!!selectedFramework} onOpenChange={() => setSelectedFramework(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <Shield className="h-6 w-6 text-primary" />
              <span className="font-display uppercase">
                {selectedFramework && (frameworkInfo[selectedFramework.framework]?.name || selectedFramework.framework)}
              </span>
              {selectedFramework && (
                <Badge className={cn(
                  "font-mono",
                  selectedFramework.score >= 80 ? "bg-success/20 text-success" : "bg-warning/20 text-warning"
                )}>
                  {selectedFramework.score >= 80 ? 'COMPLIANT' : 'NON-COMPLIANT'}
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>

          {selectedFramework && (
            <div className="space-y-4 mt-2">
              {/* Summary */}
              <div className="grid grid-cols-4 gap-3">
                <div className="text-center p-3 bg-secondary/50 rounded">
                  <p className="text-xl font-mono font-bold">{controls.length}</p>
                  <p className="text-xs text-muted-foreground">Controles</p>
                </div>
                <div className="text-center p-3 bg-success/10 rounded">
                  <p className="text-xl font-mono font-bold text-success">{passedControls.length}</p>
                  <p className="text-xs text-muted-foreground">Conformes</p>
                </div>
                <div className="text-center p-3 bg-destructive/10 rounded">
                  <p className="text-xl font-mono font-bold text-destructive">{failedControls.length}</p>
                  <p className="text-xs text-muted-foreground">Não Conformes</p>
                </div>
                <div className="text-center p-3 bg-primary/10 rounded">
                  <p className={cn("text-xl font-mono font-bold", getScoreColor(selectedFramework.score))}>
                    {selectedFramework.score}%
                  </p>
                  <p className="text-xs text-muted-foreground">Score</p>
                </div>
              </div>

              {/* Filter */}
              <div className="flex gap-3">
                <Select value={controlFilter} onValueChange={setControlFilter}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="passed">Conformes</SelectItem>
                    <SelectItem value="failed">Não Conformes</SelectItem>
                  </SelectContent>
                </Select>
                <span className="text-sm text-muted-foreground self-center">
                  {filteredControls.length} controles
                </span>
              </div>

              <ScrollArea className="h-[380px]">
                <div className="space-y-3 pr-2">
                  {controlsLoading ? (
                    <div className="flex items-center justify-center h-24">
                      <p className="text-muted-foreground">Carregando controles...</p>
                    </div>
                  ) : filteredControls.map((control) => (
                    <div
                      key={control.id}
                      className={cn(
                        "p-4 rounded-sm border transition-colors",
                        control.status === 'passed' 
                          ? "bg-success/5 border-success/30" 
                          : "bg-destructive/5 border-destructive/30"
                      )}
                      data-testid={`control-${control.id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3 flex-1">
                          {control.status === 'passed' ? (
                            <CheckCircle className="h-5 w-5 text-success flex-shrink-0 mt-0.5" />
                          ) : (
                            <XCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
                          )}
                          <div>
                            <p className="font-medium text-sm">{control.control}</p>
                            <p className="text-xs text-muted-foreground mt-1">{control.description}</p>
                            <Badge variant="outline" className="text-xs mt-2">{control.category}</Badge>
                          </div>
                        </div>
                        <Badge className={cn(
                          "border text-xs ml-3 flex-shrink-0",
                          control.status === 'passed' 
                            ? "bg-success/20 text-success border-success/30" 
                            : "bg-destructive/20 text-destructive border-destructive/30"
                        )}>
                          {control.status === 'passed' ? 'CONFORME' : 'NÃO CONFORME'}
                        </Badge>
                      </div>
                      {control.status === 'failed' && (
                        <div className="mt-3 p-2 bg-warning/10 border border-warning/20 rounded text-xs text-warning">
                          Ação necessária: Revisar e implementar este controle para atingir conformidade.
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </ScrollArea>

              <div className="flex gap-2 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => handleExportPDF(selectedFramework?.framework)} disabled={exportingPDF} data-testid="export-framework-pdf-btn">
                  {exportingPDF ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
                  Exportar Relatório {selectedFramework && (frameworkInfo[selectedFramework.framework]?.name || selectedFramework.framework)}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default CompliancePage;
